import * as React from 'react';

import { enumUnit, Weight } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  Col,
  ContentCard,
  Footer,
  Menu,
  Row,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideDot, LucideMoreHorizontal } from 'lucide-react';

import { $remoteFacilities } from '../../../_state/facilities.state';
import {
  useLoadDetailMenuItems,
  type MenuItem,
} from '../../../hooks/useLoadDetailMenuItems';
import type {
  LoadFormLineItem,
  LoadLineItemRecord,
  LoadRecord,
} from '../../../types/load';
import { enumLoadWeightType } from '../../../types/load';

import { FromFacilityToCustomerLabel } from './FromFacilityToCustomerLabel';
import { LoadDetailFooter } from './LoadDetailFooter';

import {
  SalesOrderLoadReadOnlyTable,
  StandaloneLoadTable,
} from '~/modules/trade/_components/_shared/LineItems';
import { LoadStatusBadge } from '~/modules/trade/_components/_shared/StatusBadges';
import { $salesOrderLineItems } from '~/modules/trade/_state/loadsTab.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { getLoadLineItems } from '~/modules/trade/utils/loadLineItems';
import { toMoney, zeroMoney } from '~/modules/trade/utils/money';
import {
  convertSalesOrderQuantityToWeight,
  toWeight,
  zeroWeight,
} from '~/modules/trade/utils/weight';
import { $defaultUnitSystemUnit } from '~/shared/state/settings.state';

const toFormLineItem = (
  item: LoadLineItemRecord | LoadFormLineItem,
): LoadFormLineItem => ({
  id: item.id,
  type: item.type,
  itemId: item.itemId || null,
  item: item.item ?? null,
  tagId: item.tagId || null,
  unitPrice: item.unitPrice || null,
  estimatedPrice: item.estimatedPrice || null,
  pricingUnit: item.pricingUnit,
  weights: item.weights,
});

type LoadDetailHeaderProps = {
  load: LoadRecord;
  entityType: 'booking' | 'salesOrder' | 'purchaseOrder';
  facilityName: string;
  customerName: string;
  menuItems: MenuItem[];
  onViewLoadInfo: () => void;
};

// ⚛️ ⚛️ ⚛️ LOAD DETAIL HEADER ⚛️ ⚛️ ⚛️
const LoadDetailHeader: React.FC<LoadDetailHeaderProps> = ({
  load,
  entityType,
  facilityName,
  customerName,
  menuItems,
  onViewLoadInfo,
}) => {
  const t = useTranslations();

  return (
    <Row className="@LoadDetailHeader px-6 py-4" xBetween>
      <Row gap2>
        <div>
          <Row gap3>
            <Row gap1 className="text-xl">
              <h2 className="font-semibold text-neutral/90">
                {t.loadDetail.title(load.loadNumber)}
              </h2>
              {entityType === 'booking' && load.salesOrderNumber && (
                <>
                  <LucideDot />
                  <div className="font-light text-neutral/90">
                    {t.loadDetail.secondaryTitle.salesOrder(
                      load.salesOrderNumber,
                    )}
                  </div>
                </>
              )}
              {entityType === 'salesOrder' && load.bookingNumber && (
                <>
                  <LucideDot />
                  <div className="font-light text-neutral/90">
                    {t.loadDetail.secondaryTitle.booking(load.bookingNumber)}
                  </div>
                </>
              )}
            </Row>
            <LoadStatusBadge status={load.status} isReviewed={load.reviewed} />
          </Row>
          <FromFacilityToCustomerLabel
            facilityName={facilityName}
            customerName={customerName}
          />
        </div>
      </Row>
      <Row gap2>
        <Menu
          trigger={
            <Button
              icon={<LucideMoreHorizontal className="size-4" />}
              variant="outline"
              className="w-8"
            />
          }
          items={menuItems}
        />
        <Button variant="outline" onPress={onViewLoadInfo}>
          {t.loadDetail.actions.viewLoadInfo}
        </Button>
      </Row>
    </Row>
  );
};

type LoadDetailProps = {
  load: LoadRecord;
  entityType: 'booking' | 'salesOrder' | 'purchaseOrder';
  onRefetch?: () => void;
};

// ⚛️ ⚛️ ⚛️ LOAD DETAIL ⚛️ ⚛️ ⚛️
export const LoadDetail: React.FC<LoadDetailProps> = ({
  load: loadProps,
  entityType,
  onRefetch,
}) => {
  // Atoms
  const { data: salesOrderLineItemsData, fetch: fetchSalesOrderLineItems } =
    useAtomValue($salesOrderLineItems);
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);

  const { menuItems, popovers } = useLoadDetailMenuItems(
    loadProps,
    'always',
    undefined,
    onRefetch,
  );

  const { relevantLineItems, requestedLineItems, shippedLineItems } =
    getLoadLineItems(loadProps.lineItems);

  const load = { ...loadProps, lineItems: relevantLineItems } as LoadRecord;

  const facility = facilitiesData?.facilities?.find(
    (f) => f.id === load.facilityId,
  );

  const customerData = salesOrderLineItemsData?.account ?? null;

  const salesOrderLineItems: LoadFormLineItem[] | undefined =
    React.useMemo(() => {
      if (load.salesOrderId === null) return undefined;
      if (!salesOrderLineItemsData) return undefined;

      return salesOrderLineItemsData.lineItems.map((lineItem) => ({
        id: lineItem.id,
        type: 'Raw' as const,
        itemId: lineItem.item.id,
        tagId: null,
        pricingUnit: lineItem.unit,
        unitPrice: toMoney(lineItem.unitPrice),
        estimatedPrice: toMoney(lineItem.totalPrice ?? zeroMoney()),
        weights: {
          [enumLoadWeightType.Net]: toWeight(
            Number(lineItem.quantity),
            enumUnit.Pounds,
          ),
          [enumLoadWeightType.Gross]: toWeight(
            Number(lineItem.quantity),
            enumUnit.Pounds,
          ),
          [enumLoadWeightType.Tare]: zeroWeight(),
          [enumLoadWeightType.QuantityOwed]: toWeight(
            Number(lineItem.quantity),
            enumUnit.Pounds,
          ),
        },
      }));
    }, [load.salesOrderId, salesOrderLineItemsData]);

  const salesOrderLineItemsMap = React.useMemo(() => {
    if (!salesOrderLineItemsData?.lineItems)
      return new Map<
        string,
        {
          quantityOwedWeight: Weight;
        }
      >();

    return new Map(
      salesOrderLineItemsData.lineItems.map((li) => [
        li.item.id,
        {
          quantityOwedWeight: convertSalesOrderQuantityToWeight(
            Number(li.quantity),
            li.unit,
          ),
        },
      ]),
    );
  }, [salesOrderLineItemsData]);

  const allLineItems: LoadFormLineItem[] = React.useMemo(
    () => requestedLineItems?.map(toFormLineItem) || [],
    [requestedLineItems],
  );

  const shippedFormLineItems: LoadFormLineItem[] = React.useMemo(
    () => shippedLineItems.map(toFormLineItem),
    [shippedLineItems],
  );

  const { salesOrderLineItemsList, standaloneLineItemsList } =
    React.useMemo(() => {
      if (!load.salesOrderId || salesOrderLineItemsMap.size === 0) {
        return {
          salesOrderLineItemsList: [] as LoadFormLineItem[],
          standaloneLineItemsList: allLineItems,
        };
      }

      const soItems: LoadFormLineItem[] = [];
      const standalone: LoadFormLineItem[] = [];

      for (const item of allLineItems) {
        const soData = item.itemId
          ? salesOrderLineItemsMap.get(item.itemId)
          : undefined;
        if (soData) {
          soItems.push({
            ...item,
            weights: {
              ...item.weights,
              [enumLoadWeightType.QuantityOwed]: soData.quantityOwedWeight,
            },
          });
        } else {
          standalone.push(item);
        }
      }

      return {
        salesOrderLineItemsList: soItems,
        standaloneLineItemsList: standalone,
      };
    }, [allLineItems, salesOrderLineItemsMap, load.salesOrderId]);

  const numberOfTags = React.useMemo(
    () => load.lineItems?.filter((item) => item.tagId).length ?? 0,
    [load.lineItems],
  );

  const numberOfMaterials = React.useMemo(
    () => new Set(load.lineItems?.map((item) => item.itemId)).size,
    [load.lineItems],
  );

  // Callbacks
  const handleViewLoadInfo = React.useCallback(() => {
    window.open(`/trade/loads/${load.id}`, '_blank');
  }, [load.id]);

  // Effects
  /** Fetch sales order line items for the current load */
  React.useEffect(() => {
    if (load.salesOrderId) {
      fetchSalesOrderLineItems({ salesOrderId: load.salesOrderId });
    }
  }, [fetchSalesOrderLineItems, load.salesOrderId]);
  return (
    <ContentCard className="@LoadDetail flex h-full flex-col p-0">
      <Col className="h-full">
        <LoadDetailHeader
          load={load}
          entityType={entityType}
          facilityName={facility?.facilityName ?? ''}
          customerName={customerData?.name ?? ''}
          menuItems={menuItems}
          onViewLoadInfo={handleViewLoadInfo}
        />
        <Col gap4 className="flex-1 overflow-y-auto px-6 pb-6">
          {load.salesOrderId && salesOrderLineItemsList.length > 0 && (
            <div
              className={cn(
                standaloneLineItemsList.length > 0 &&
                  'border-b border-neutral/10 pb-4',
              )}
            >
              <SalesOrderLoadReadOnlyTable
                lineItems={salesOrderLineItemsList}
                status={load.status}
                weightUnit="price"
                facilityDefaultUnit={facilityDefaultUnit}
              />
            </div>
          )}
          {standaloneLineItemsList.length > 0 && (
            <StandaloneLoadTable
              lineItems={standaloneLineItemsList}
              disabled
              hideActions
              hideAddButton
              weightUnit="price"
              facilityDefaultUnit={facilityDefaultUnit}
              onUpdateLineItem={() => null}
              onDeleteLineItem={() => null}
            />
          )}
        </Col>

        <Footer className="@LoadDetailFooter flex-shrink-0" displayShadow>
          <LoadDetailFooter
            load={loadProps}
            numberOfMaterials={numberOfMaterials}
            numberOfTags={numberOfTags}
            requestedLineItems={allLineItems}
            shippedLineItems={shippedFormLineItems}
            salesOrderLineItems={salesOrderLineItems}
            facilityDefaultUnit={facilityDefaultUnit}
            onRefetch={onRefetch}
          />
        </Footer>
      </Col>
      {popovers}
    </ContentCard>
  );
};
